import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ContentCopy as CloneIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import PositionsModal from './PositionsModal';
import { useTranslation } from 'react-i18next';
import HiringRoomImportModal from './HiringRoomImportModal';
import AtsDeletePositionDialog from '../ats/positions/AtsDeletePositionDialog';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';

const PositionsLayout = () => {
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view', 'clone'
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);
  const [hiringRoomConnected, setHiringRoomConnected] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { authToken, showAlert, user, project, organization } = useStore.getState();
  const { t } = useTranslation();

  // Función para formatear fechas
  const formatDate = (dateValue) => {
    if (!dateValue) return t('positions.notAvailable');

    try {
      let date;

      // Si es un timestamp de Firestore serializado {_seconds, _nanoseconds}
      if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      }
      // Si es un timestamp de Firestore con toDate()
      else if (dateValue && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      }
      // Si es un objeto con seconds (formato Firestore)
      else if (dateValue && dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      }
      // Si ya es un objeto Date
      else if (dateValue instanceof Date) {
        date = dateValue;
      }
      // Si es un string de fecha
      else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      }
      // Si es un timestamp en milisegundos
      else if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      }
      else {
        return t('positions.notAvailable');
      }

      // Verificar que la fecha sea válida
      if (isNaN(date.getTime())) {
        return t('positions.notAvailable');
      }

      // Formatear la fecha en español
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('positions.notAvailable');
    }
  };

  // Función para filtrar cargos - ACTUALIZADA con nuevos campos
  const filterPositions = (positions, searchTerm) => {
    if (!searchTerm.trim()) {
      return positions;
    }

    const term = searchTerm.toLowerCase();
    return positions.filter((position) => {
      return position.name?.toLowerCase().includes(term);
    });
  };

  const checkHiringRoomConnection = async () => {
    try {
      const response = await api.get(
        '/projects/hiringroom-configuration',
        {
          params: {
            organizationId: organization,
            projectId: project
          },
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data.response.status_response === 'OK') {
        const config = response.data.response.data;
        setHiringRoomConnected(config?.connected === true);
      }
    } catch (err) {
      console.error('Error verificando conexión HiringRoom:', err);
    }
  };

  useEffect(() => {
    if (authToken && organization && project) {
      checkHiringRoomConnection();
    }
  }, [authToken, organization, project]);

  const handleImportComplete = () => {
    // Recargar positions después de importar
    loadPositions(); // O como sea que se llame tu función para cargar positions
  };

  // Efecto para filtrar cuando cambia el término de búsqueda o las posiciones
  useEffect(() => {
    const filtered = filterPositions(positions, searchTerm);
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
    setFilteredPositions(filtered);
    setPage(0);
  }, [positions, searchTerm]);

  // Función para limpiar búsqueda
  const clearSearch = () => {
    setSearchTerm('');
  };

  // API Functions
  const getAllPositions = async () => {
    const response = await api.get("/positions", {
      params: { projectId: project },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  useEffect(() => {
    loadPositions();
  }, []);

  const loadPositions = async () => {
    try {
      setLoading(true);
      const response = await getAllPositions();
      if (response.response && response.response.status_response === 'OK') {
        setPositions(response.response.data || []);
      } else {
        setError(t('positions.loadError'));
        showAlert(t('positions.loadError'), "warning");
      }
    } catch (err) {
      setError(t('generalApiError'));
      showAlert(t('generalApiError'), "error");
      console.error('Error loading positions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, position = null) => {
    setModalMode(mode);

    // Si es modo clonar, modificar el nombre para agregar "- Copia"
    if (mode === 'clone' && position) {
      const clonedPosition = {
        ...position,
        name: `${position.name} ${t('positions.copySuffix')}`,
        // Remover campos que no deben clonarse
        id: undefined,
        uid: undefined,
        created: undefined,
        modified: undefined,
        lastUpdatedBy: undefined
      };
      setSelectedPosition(clonedPosition);
    } else {
      setSelectedPosition(position);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPosition(null);
    setError(null);
  };

  const handleSaveModal = () => {
    loadPositions();
  };

  const handleDelete = (position) => {
    setPositionToDelete(position);
    setOpenDeleteDialog(true);
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setPositionToDelete(null);
  };

  if (loading && positions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('positions.title')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Barra de búsqueda compacta */}
          <TextField
            variant="outlined"
            placeholder={t('positions.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />

          {/* Botón Importar con HiringRoom - Solo visible si está conectado */}
          {hiringRoomConnected && (
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={() => setOpenImportModal(true)}
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: '#ecfdf5'
                }
              }}
            >
              {t('positions.importFromHiringRoom')}
            </Button>
          )}

          {/* Botón para nuevo cargo */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal('create')}
          >
            {t('positions.newPosition')}
          </Button>
        </Box>
      </Box>

      {/* Mostrar error si existe */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla de cargos */}
      <TableContainer
        component={Paper}
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 230px)',
          overflowY: 'auto',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>{t('name')}</strong></TableCell>
              <TableCell><strong>{t('industry')}</strong></TableCell>
              <TableCell><strong>{t('positions.hierarchy')}</strong></TableCell>
              <TableCell><strong>{t('positions.mission')}</strong></TableCell>
              <TableCell><strong>{t('createdAt')}</strong></TableCell>
              <TableCell><strong>{t('lastModified')}</strong></TableCell>
              <TableCell><strong>{t('actions')}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPositions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm
                      ? `${t('positions.noPositionsFound')} "${searchTerm}"`
                      : t('positions.noPositions')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPositions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((position) => (
                <TableRow key={position.id || position.uid}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {position.name}
                      </Typography>
                      {position.hiringRoomVacancyId && (
                        <Chip
                          label={t('positions.hiringRoomBadge')}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      )}
                      {(position.source === 'ats' || position.atsEnabled === true) && (
                        <Chip
                          label={t('positions.atsBadge')}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: '20px',
                            color: '#6A5AF9',
                            borderColor: '#6A5AF9',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={position.industria}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {position.jerarquia || t('positions.notAvailable')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {position.descripcion}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(position.created)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(position.modified)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal('view', position)}
                      title={t('show')}
                    >
                      <ViewIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal('edit', position)}
                      title={t('edit')}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal('clone', position)}
                      title={t('clone')}
                      color="info"
                    >
                      <CloneIcon />
                    </IconButton>


                    <IconButton
                      size="small"
                      onClick={() => handleDelete(position)}
                      title={t('delete')}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>

                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredPositions.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        sx={{ mr: 6 }}
      />

      {/* Modal de confirmación para eliminar */}


      {/* Modal para crear/editar/ver/clonar */}
      <PositionsModal
        open={openModal}
        onClose={handleCloseModal}
        mode={modalMode === 'clone' ? 'create' : modalMode} // El modal recibe 'create' para clonado
        selectedPosition={selectedPosition}
        onSave={handleSaveModal}
        user={user}
        authToken={authToken}
        showAlert={showAlert}
        project={project} // Pasar project al modal
      />

      {/* Confirmación para eliminar: el mismo modal del ATS (escribir la
          palabra de confirmación, candidatos y aviso del borrado lógico) */}
      <AtsDeletePositionDialog
        open={openDeleteDialog}
        positionId={positionToDelete?.id || positionToDelete?.uid}
        positionName={positionToDelete?.name}
        candidatesCount={typeof positionToDelete?.totalApplications === 'number' ? positionToDelete.totalApplications : null}
        onClose={cancelDelete}
        onDeleted={() => loadPositions()}
      />

      {/* Modal de importación de HiringRoom */}
      <HiringRoomImportModal
        open={openImportModal}
        onClose={() => setOpenImportModal(false)}
        onImportComplete={handleImportComplete}
      />
    </Box>
  );
};

export default PositionsLayout;